<script setup>
/* Il pannello del menu contestuale. Uno solo, montato accanto ai toast e alle
 * modali: vedi stores/menu.js per il perché.
 *
 * Si comporta come un menu nativo e non come un pannello web qualsiasi:
 *  - appare col vertice sul puntatore, e si ribalta se sfonderebbe il bordo
 *    dello schermo (a destra si apre a sinistra, in basso verso l'alto);
 *  - le frecce lo percorrono, Invio sceglie, Esc chiude e il fuoco torna da
 *    dove è partito;
 *  - si chiude a qualunque cosa lo scavalchi: un clic fuori, uno scorrimento,
 *    un ridimensionamento, la finestra che perde il fuoco. Un menu rimasto
 *    appeso sopra una griglia che nel frattempo è scorsa punterebbe a un
 *    oggetto che non è più lì sotto.
 *
 * L'azione della voce viene chiamata DOPO la chiusura: quasi tutte aprono una
 * modale, e un menu ancora aperto sotto la modale resterebbe visibile ai suoi
 * bordi e ruberebbe il fuoco che la modale sta cercando di prendere.
 */
import { nextTick, onBeforeUnmount, ref, watch } from 'vue';

import { useMenuStore } from '@/stores/menu';

const menu = useMenuStore();

const pannello = ref(null);
/* La posizione vera, calcolata dopo la misura. Parte da quella chiesta: al
 * primo frame il pannello non è ancora stato misurato e disegnarlo altrove
 * lo farebbe saltare sotto gli occhi. */
const posto = ref({ left: 0, top: 0 });
/* Dove tornerà il fuoco alla chiusura. Senza, dopo Esc si finisce sul body e
 * il Tab riparte dall'inizio della pagina. */
let venivaDa = null;

const MARGINE = 8;

watch(
  () => menu.aperto,
  async (adesso) => {
    if (!adesso) {
      const torna = venivaDa;
      venivaDa = null;
      // Solo se è ancora nella pagina: la voce può aver rimosso l'elemento.
      if (torna?.isConnected) torna.focus();
      return;
    }

    venivaDa = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    posto.value = { left: menu.x, top: menu.y };
    await nextTick();
    collocaEFocalizza();
  },
);

function collocaEFocalizza() {
  const nodo = pannello.value;
  if (!nodo) return;
  const { width, height } = nodo.getBoundingClientRect();
  const largo = window.innerWidth;
  const alto = window.innerHeight;

  /* Prima si prova a ribaltare — è quello che fa un menu di sistema, e tiene
   * il puntatore su un angolo del pannello invece che in mezzo a un lato. Se
   * ribaltato non ci sta comunque (menu più alto della finestra), si incolla
   * al bordo. */
  let left = menu.x;
  if (left + width + MARGINE > largo) left = menu.x - width;
  left = Math.max(MARGINE, Math.min(left, largo - width - MARGINE));

  let top = menu.y;
  if (top + height + MARGINE > alto) top = menu.y - height;
  top = Math.max(MARGINE, Math.min(top, alto - height - MARGINE));

  posto.value = { left, top };
  nodo.querySelector('[data-voce]')?.focus();
}

function scegli(voce) {
  if (voce.disabilitata) return;
  menu.chiudi();
  voce.azione?.();
}

/* --- Tastiera ----------------------------------------------------------- */

function scorri(passo) {
  const voci = [...(pannello.value?.querySelectorAll('[data-voce]:not([disabled])') ?? [])];
  if (!voci.length) return;
  const ora = voci.indexOf(document.activeElement);
  // Il modulo fa girare: dall'ultima si torna alla prima, come nei menu veri.
  const prossima = (ora + passo + voci.length) % voci.length;
  voci[ora < 0 && passo < 0 ? voci.length - 1 : prossima].focus();
}

function ultima() {
  const voci = [...(pannello.value?.querySelectorAll('[data-voce]:not([disabled])') ?? [])];
  voci.at(-1)?.focus();
}

function tasto(evento) {
  if (evento.key === 'Escape') menu.chiudi();
  else if (evento.key === 'ArrowDown') scorri(1);
  else if (evento.key === 'ArrowUp') scorri(-1);
  else if (evento.key === 'Home') pannello.value?.querySelector('[data-voce]')?.focus();
  else if (evento.key === 'End') ultima();
  // Il Tab non naviga dentro un menu: lo chiude, come ovunque.
  else if (evento.key === 'Tab') menu.chiudi();
  else return;
  evento.preventDefault();
}

/* --- Chiusure dall'esterno ---------------------------------------------- */

function fuori(evento) {
  if (!pannello.value?.contains(evento.target)) menu.chiudi();
}

const viaSubito = () => menu.chiudi();

watch(
  () => menu.aperto,
  (adesso) => {
    if (adesso) ascolta();
    else smetti();
  },
);

function ascolta() {
  // In cattura: un clic su un elemento che ferma la propagazione deve
  // comunque chiudere il menu.
  document.addEventListener('pointerdown', fuori, true);
  // Passivo e in cattura: lo scorrimento di QUALUNQUE contenitore, non solo
  // della pagina (la colonna delle board scorre per conto suo).
  document.addEventListener('scroll', viaSubito, { capture: true, passive: true });
  window.addEventListener('resize', viaSubito);
  window.addEventListener('blur', viaSubito);
}

function smetti() {
  document.removeEventListener('pointerdown', fuori, true);
  document.removeEventListener('scroll', viaSubito, { capture: true });
  window.removeEventListener('resize', viaSubito);
  window.removeEventListener('blur', viaSubito);
}

onBeforeUnmount(smetti);
</script>

<template>
  <Teleport to="body">
    <Transition name="menu">
      <div
        v-if="menu.aperto"
        ref="pannello"
        class="menu"
        role="menu"
        :style="{ left: `${posto.left}px`, top: `${posto.top}px` }"
        :aria-label="menu.titolo || undefined"
        @keydown="tasto"
        @contextmenu.prevent
      >
        <p v-if="menu.titolo" class="menu__titolo label">{{ menu.titolo }}</p>

        <template v-for="(voce, indice) in menu.voci" :key="indice">
          <hr v-if="voce.separatore" class="menu__filetto" />
          <button
            v-else
            data-voce
            class="menu__voce"
            :class="{ 'menu__voce--pericolo': voce.pericolo }"
            type="button"
            role="menuitem"
            :disabled="voce.disabilitata"
            @click="scegli(voce)"
          >
            <span class="menu__testo">{{ voce.etichetta }}</span>
            <span v-if="voce.spunta" class="menu__spunta" aria-hidden="true">
              <svg viewBox="0 0 16 16" width="12" height="12">
                <path d="M3 8.5l3.2 3.2L13 5" stroke="currentColor" stroke-width="1.8" fill="none" />
              </svg>
            </span>
          </button>
        </template>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Sopra ogni cosa tranne le modali (che stanno più in alto e comunque lo
   chiudono): la colonna sta a 41, i toast a 80. */
.menu {
  position: fixed;
  z-index: 90;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 12rem;
  max-width: 18rem;
  padding: 5px;
  border: 1px solid var(--rule-strong);
  border-radius: var(--radius-lg);
  background: var(--surface-raised);
  box-shadow: var(--shadow-raised);
}

.menu__titolo {
  overflow: hidden;
  padding: 5px 9px 6px;
  color: var(--ink-faint);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.menu__voce {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--s-3);
  width: 100%;
  padding: 6px 9px;
  border: 0;
  border-radius: var(--radius);
  background: transparent;
  color: var(--ink);
  font: inherit;
  font-size: var(--t-sm);
  text-align: left;
  cursor: pointer;
}

.menu__voce:hover,
.menu__voce:focus-visible {
  background: var(--accent);
  color: var(--accent-contrast);
  outline: none;
}

.menu__voce[disabled] {
  color: var(--ink-faint);
  cursor: default;
}

.menu__voce[disabled]:hover {
  background: transparent;
  color: var(--ink-faint);
}

.menu__voce--pericolo {
  color: var(--danger);
}

.menu__voce--pericolo:hover,
.menu__voce--pericolo:focus-visible {
  background: var(--danger);
  color: #fff;
}

.menu__testo {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.menu__spunta {
  display: grid;
  flex: none;
  place-items: center;
}

.menu__filetto {
  height: 0;
  margin: 4px 6px;
  border: 0;
  border-top: 1px solid var(--rule);
}

/* Appena accennata: un menu contestuale deve *essere già lì* quando l'occhio
   ci arriva. Un'animazione più lunga si legge come ritardo. */
.menu-enter-active {
  transition: opacity 90ms var(--ease), scale 90ms var(--ease);
  transform-origin: top left;
}

.menu-enter-from {
  opacity: 0;
  scale: 0.97;
}
</style>
